/**
 * Status Override Mutation Hooks
 *
 * Wraps tRPC mutations for overriding order and garment statuses.
 * Invalidates relevant tracker caches on success.
 */

import { trpc } from '@/lib/trpc';

/**
 * Mutation hook for overriding an order's production status.
 */
export function useOverrideOrderStatus() {
  const utils = trpc.useUtils();

  return trpc.productionOrders.overrideOrderStatus.useMutation({
    onSuccess: () => {
      utils.productionOrders.getBatchOrdersWithGarments.invalidate();
      utils.productionOrders.listTrackerOrders.invalidate();
      utils.productionOrders.getTrackerCounts.invalidate();
      utils.productionOrders.listBatches.invalidate();
      utils.filmPrint.listDtfBatches.invalidate();
    },
  });
}

/**
 * Mutation hook for overriding a garment's status.
 */
export function useOverrideGarmentStatus() {
  const utils = trpc.useUtils();

  return trpc.productionOrders.overrideGarmentStatus.useMutation({
    onSuccess: () => {
      utils.productionOrders.getOrderGarments.invalidate();
      utils.productionOrders.getBatchOrdersWithGarments.invalidate();
      utils.productionOrders.listTrackerOrders.invalidate();
      utils.productionOrders.getTrackerCounts.invalidate();
    },
  });
}

/**
 * Where a DTF/DTG switch was asked from: the expanded order and the batch the
 * garments were loaded under. Both optional (the Orders view has no batch).
 */
export interface PrintTechnologySwitchScope {
  orderId?: string | null;
  batchId?: string | null;
}

/**
 * The narrow refresh after a DTF/DTG switch (Brett's rule, BLU-1509): the
 * order's garments, each touched batch's garments, and the film station's
 * batch list (a batch can gain or lose its last DTF garment). Never the
 * tracker's order list or tab counts: an in-place switch changes no status,
 * so neither moves.
 */
function useInvalidateAfterSwitch() {
  const utils = trpc.useUtils();
  return (orderId: string | null | undefined, batchIds: ReadonlyArray<string | null | undefined>) => {
    if (orderId) void utils.productionOrders.getOrderGarments.invalidate({ orderId });
    for (const batchId of new Set(batchIds)) {
      if (batchId) void utils.productionOrders.getBatchOrdersWithGarments.invalidate({ batchId });
    }
    void utils.filmPrint.listDtfBatches.invalidate();
  };
}

/**
 * BLU-1412 / BLU-1509: switch one garment between DTF and DTG, in place.
 * Refreshes on EVERY response, refusals included: a refusal usually means the
 * garment moved on (or was already switched) since the row was drawn.
 */
export function useSwitchGarmentPrintTechnology(scope: PrintTechnologySwitchScope = {}) {
  const invalidate = useInvalidateAfterSwitch();

  return trpc.productionOrders.switchGarmentPrintTechnology.useMutation({
    onSettled: (data) => {
      const switched = data?.ok ? data : null;
      invalidate(switched?.prodOrderId ?? scope.orderId, [switched?.batchId, scope.batchId]);
    },
  });
}

/**
 * BLU-1509: switch every garment of one order that can still switch, in one
 * call. Same narrow refresh: the order, every batch a garment was switched
 * in, and the batch the order was opened from (its rows may be stale even
 * when nothing switched, e.g. a retried call).
 */
export function useSwitchOrderPrintTechnology(scope: PrintTechnologySwitchScope = {}) {
  const invalidate = useInvalidateAfterSwitch();

  return trpc.productionOrders.switchOrderPrintTechnology.useMutation({
    onSettled: (data) => {
      invalidate(data?.prodOrderId ?? scope.orderId, [
        ...(data?.flipped ?? []).map((f) => f.batchId),
        scope.batchId,
      ]);
    },
  });
}

/**
 * BLU-1462: client QC redo. Refreshes the expanded order, the order lists and
 * the tab counts (the reopened order moves from Shipped back to Active).
 *
 * `listRefetchedByCaller`: the caller moves the tracker to a new list query
 * on success (tab + search), which fetches fresh by itself. The order lists
 * are then only marked stale, not refetched, so one success costs one list
 * fetch instead of two.
 */
export function useCreateClientQcRedo({ listRefetchedByCaller = false }: { listRefetchedByCaller?: boolean } = {}) {
  const utils = trpc.useUtils();

  return trpc.productionOrders.createClientQcRedo.useMutation({
    onSettled: (data) => {
      const skipListRefetch = listRefetchedByCaller && data?.ok === true;
      utils.productionOrders.getOrderGarments.invalidate();
      utils.productionOrders.getBatchOrdersWithGarments.invalidate();
      utils.productionOrders.listTrackerOrders.invalidate(undefined, skipListRefetch ? { refetchType: 'none' } : undefined);
      utils.productionOrders.getTrackerCounts.invalidate();
    },
  });
}
